/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import {
  Boton,
  Titulo,
  accessAPI,
  formatFechaHora,
  Sidebar,
  tituloBase,
  TablaFiltrable
} from "../../../../shared";

export default function Enviados() {
  const [loader, setLoader] = useState(true);
  const [enviados, setEnviados] = useState([]);
  const [paginas, setPaginas] = useState(1);
  const [rangos, setRangos] = useState([]);
  const [paginaSeleccionada, setPaginaSeleccionada] = useState(1);
  const [toggleConEnvio, setToggleConEnvio] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [busquedaAplicada, setBusquedaAplicada] = useState("");

  const [sideBarDetalle, setSideBarDetalle] = useState(false);
  const [eventoDetalle, setEventoDetalle] = useState(null);
  const [loaderDetalle, setLoaderDetalle] = useState(false);

  const toast = useRef(null);
  const selectRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Enviados";
    return () => (document.title = tituloBase());
  }, []);

  useEffect(() => {
    setLoader(true);

    let url = `admin/comunicacion/mensajes/${paginaSeleccionada}`;
    const params = [];
    if (toggleConEnvio) params.push("envio=true");
    if (busquedaAplicada) params.push(`busqueda=${encodeURIComponent(busquedaAplicada)}`);
    if (params.length) url += `?${params.join("&")}`;

    accessAPI(
      "GET",
      url,
      null,
      (respuesta) => {
        setEnviados(respuesta.eventos);
        setPaginas(respuesta.paginas);
        setRangos(respuesta.rangos || []);
        setLoader(false);
      },
      (respuesta) => {
        setPaginaSeleccionada(1);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar comunicaciones",
          sticky: true
        });
        setLoader(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginaSeleccionada, toggleConEnvio, busquedaAplicada]);

  function verDetalle(eventoId) {
    setLoaderDetalle(true);
    setEventoDetalle(null);
    setSideBarDetalle(true);
    accessAPI(
      "GET",
      `admin/comunicacion/mensajes/detalle/${eventoId}`,
      null,
      (respuesta) => {
        setEventoDetalle(respuesta);
        setLoaderDetalle(false);
      },
      (respuesta) => {
        setLoaderDetalle(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar detalle",
          sticky: true
        });
        setSideBarDetalle(false);
      }
    );
  }

  function armarPaginador() {
    const opciones = [];
    for (let i = 1; i <= paginas; i++) {
      opciones.push(
        <option value={i} key={i}>
          Página {i}
          {rangos[i - 1] &&
            ` · ${formatFechaHora(rangos[i - 1].desde)} – ${formatFechaHora(
              rangos[i - 1].hasta
            )}`}
        </option>
      );
    }
    return opciones;
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={sideBarDetalle}
        onHide={() => setSideBarDetalle(false)}
        position="right"
        className="w-full lg:w-8"
      >
        <Titulo
          botones={
            eventoDetalle?.eventotiponombre === "ENVIO_COMUNICACION" &&
            eventoDetalle.parametros?.bccFallidos?.length > 0 && (
              <Boton
                azul
                callback={() =>
                  navigate(`/comunicaciones?reenvio=${eventoDetalle.id}`)
                }
              >
                Reenviar a fallidos ({eventoDetalle.parametros.bccFallidos.length})
              </Boton>
            )
          }
        >
          Detalle del evento
        </Titulo>
        {loaderDetalle && (
          <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} width="100%" height="1.5rem" />
            ))}
          </div>
        )}
        {eventoDetalle && (
          <div css={{ display: "flex", flexDirection: "column", gap: "0.3em" }}>
            <div>
              <span css={{ color: "var(--grisTextos)" }}>Creado en: </span>
              {formatFechaHora(eventoDetalle.fechacreacion)}
            </div>
            <div>
              <span css={{ color: "var(--grisTextos)" }}>Evento: </span>
              {eventoDetalle.eventotiponombre}
            </div>
            <div>
              <span css={{ color: "var(--grisTextos)" }}>Parámetros: </span>
              <span css={{ whiteSpace: "pre-line" }}>
                {JSON.stringify(eventoDetalle.parametros)}
              </span>
            </div>
            {eventoDetalle.eventoprocesado[0]?.logProcesado && (
              <div>
                <span css={{ color: "var(--grisTextos)" }}>Log procesado: </span>
                <span css={{ whiteSpace: "pre-line" }}>
                  {JSON.stringify(eventoDetalle.eventoprocesado[0].logProcesado)}
                </span>
              </div>
            )}

            {eventoDetalle.eventoprocesado.map((proc, index) => (
              <div key={index}>
                <Divider />
                <div css={{ fontWeight: "bold", marginBottom: "0.5em" }}>
                  Comunicación enviada
                </div>
                <div css={{ display: "flex", flexDirection: "column", gap: "0.3em" }}>
                  <div>
                    <span css={{ color: "var(--grisTextos)" }}>Enviado: </span>
                    {formatFechaHora(proc.fechaprocesado)}
                  </div>
                  <div>
                    <span css={{ color: "var(--grisTextos)" }}>Destinatario: </span>
                    {proc.textoenviado?.destinatario}
                  </div>
                  {proc.textoenviado?.bccemail && (
                    <div>
                      <span css={{ color: "var(--grisTextos)" }}>BCC: </span>
                      {proc.textoenviado.bccemail}
                    </div>
                  )}
                  {proc.textoenviado?.replytoemail && (
                    <div>
                      <span css={{ color: "var(--grisTextos)" }}>Reply-To: </span>
                      {proc.textoenviado.replytoemail}
                    </div>
                  )}
                  <div>
                    <span css={{ color: "var(--grisTextos)" }}>Asunto: </span>
                    {proc.textoenviado?.asunto}
                  </div>
                  <div>
                    <span css={{ color: "var(--grisTextos)" }}>Texto: </span>
                    <span css={{ whiteSpace: "pre-line" }}>
                      {proc.textoenviado?.texto}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Sidebar>

      {loader && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem"
            }}
          >
            <Skeleton width="15rem" height="2.5rem" />
            <Skeleton width="10rem" height="2.5rem" />
          </div>
          <Card>
            <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <Skeleton width="100%" height="3rem" />
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} width="100%" height="3.5rem" />
              ))}
            </div>
          </Card>
        </div>
      )}

      {!loader && (
        <div>
          <Titulo
            urlVolver="/comunicacion/configeventos"
            botones={
              <Boton
                transparente
                callback={() => {
                  setPaginaSeleccionada(1);
                  setToggleConEnvio(!toggleConEnvio);
                }}
              >
                {toggleConEnvio ? "Ver todos" : "Ver sólo enviados"}
              </Boton>
            }
          >
            Enviados
          </Titulo>

          <Card>
            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: "1em",
                marginBottom: "1em"
              }}
            >
              {paginas > 1 && (
                <select
                  ref={selectRef}
                  value={paginaSeleccionada}
                  onChange={() => setPaginaSeleccionada(parseInt(selectRef.current.value))}
                  css={{
                    padding: "0.5em 1em",
                    borderRadius: "6px",
                    border: "1px solid var(--surface-border)",
                    fontSize: "0.9rem"
                  }}
                >
                  {armarPaginador()}
                </select>
              )}
              <span
                className="p-input-icon-left"
                css={{
                  marginLeft: "auto",
                  width: "33%",
                  "& .pi-search": { left: "0.75rem" },
                  "@media (max-width: 768px)": { width: "100%" }
                }}
              >
                <i className="pi pi-search" />
                <InputText
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setPaginaSeleccionada(1);
                      setBusquedaAplicada(busqueda);
                    }
                  }}
                  placeholder="Buscar..."
                  css={{ width: "100%", paddingLeft: "2.5rem" }}
                />
              </span>
            </div>
            <TablaFiltrable conFiltros={false}
              value={enviados}
              stripedRows
              emptyMessage="No hay eventos"
              onRowClick={(e) => verDetalle(e.data.id)}
              nombreArchivo="eventos_enviados"
              css={{
                "& .p-datatable-tbody > tr": { cursor: "pointer" }
              }}
            >
              <Column
                header="Fecha"
                body={(evento) => formatFechaHora(evento.fechacreacion)}
                style={{ width: "12em" }}
              />
              <Column
                header="Evento"
                field="eventotiponombre"
              />
            </TablaFiltrable>
          </Card>
        </div>
      )}
    </>
  );
}
